import { Logic } from '~common/ui-logic/logic'
import { executeTask } from '~common/ui-logic/tasks'
import type { TaskState } from '~/services/cache/types'
import type { AppContextType } from '~/app-context'

export interface GmailAccountSelectorDependencies {}

export interface GmailAccountSelectorState {
    loadState: TaskState
    currentUserEmail?: string
    googleAccounts: string[]
    profilePictures: Record<string, string | null>
    error?: string | null
}

export class GmailAccountSelectorLogic extends Logic<
    GmailAccountSelectorDependencies & AppContextType,
    GmailAccountSelectorState
> {
    getInitialState = (): GmailAccountSelectorState => ({
        loadState: 'pristine',
        currentUserEmail:
            this.deps.services.cache.getKey('activeGmailAccount') ??
            this.deps.services.cache.getKey('authState')?.data?.email ??
            undefined,
        googleAccounts: [],
        profilePictures: {},
    })

    async initialize() {
        await executeTask(this, 'loadState', async () => {
            const authState = this.deps.services.cache.getKey('authState')
            const user = authState?.data
            const userId = authState?.user?.uid
            const activeGmailAccount =
                this.deps.services.cache.getKey('activeGmailAccount')

            if (activeGmailAccount) {
                this.setState({ currentUserEmail: activeGmailAccount })
            } else if (user?.email) {
                this.setState({ currentUserEmail: user.email })
                // Initialize active account with main email if not set
                this.deps.services.cache.setActiveGmailAccount(user.email)
            }

            if (userId) {
                const accounts =
                    await this.deps.storageModules.auth.getAllGoogleAccountEmails(
                        userId,
                    )
                // Ensure current user is in the list if valid
                const allAccounts = new Set(accounts)
                if (user?.email) {
                    allAccounts.add(user.email)
                }
                const accountList = Array.from(allAccounts)

                this.setState({ googleAccounts: accountList })

                // Load profile pictures for all accounts
                accountList.forEach((email) => this.loadProfilePicture(email))
            }
        })

        this.deps.services.cache.onUpdate.listen((keys) => {
            if (keys.has('authState')) {
                const authState = this.deps.services.cache.getKey('authState')
                const user = authState?.data

                if (user?.email !== this.state.currentUserEmail) {
                    this.setState({ currentUserEmail: user?.email })
                }

                // Re-fetch accounts if user changed (e.g. login/logout)
                // Or if we just want to refresh just in case
                if (authState?.user?.uid) {
                    // For now, simplistic re-check not implemented to avoid infinite loops or over-fetching
                    // But ideally we should re-fetch googleAccounts if the user changes
                }
            }
        })
    }

    private async loadProfilePicture(email: string) {
        try {
            const base64 =
                await this.deps.storageModules.profilePictures.getProfilePictureBase64(
                    email,
                )
            this.setState({
                profilePictures: {
                    ...this.state.profilePictures,
                    [email]: base64,
                },
            })
        } catch (e) {
            // ignore missing profile pic
            this.setState({
                profilePictures: {
                    ...this.state.profilePictures,
                    [email]: null,
                },
            })
        }
    }

    handleAccountSelect = (email: string) => {
        this.setState({ currentUserEmail: email })
        this.deps.services.cache.setActiveGmailAccount(email)
    }

    handleAddNewInbox = async () => {
        await executeTask(this, 'loadState', async () => {
            try {
                const result = await this.deps.services.auth.loginWithGoogle()
                if (!result) {
                    throw new Error('Failed to login with Google')
                }
                // Refresh accounts list after successful login
                const userId =
                    this.deps.services.cache.getKey('authState')?.user?.uid
                if (userId) {
                    const accounts =
                        await this.deps.storageModules.auth.getAllGoogleAccountEmails(
                            userId,
                        )
                    const user =
                        this.deps.services.cache.getKey('authState')?.data
                    const allAccounts = new Set(accounts)
                    if (user?.email) {
                        allAccounts.add(user.email)
                    }
                    const accountList = Array.from(allAccounts)
                    this.setState({ googleAccounts: accountList })
                    accountList.forEach((email) =>
                        this.loadProfilePicture(email),
                    )
                }
            } catch (error) {
                console.error(error)
                let errorMessage = 'Sign-in failed'
                if (error instanceof Error) {
                    errorMessage = error.message
                }
                this.setState({ error: errorMessage })
            }
        })
    }
}
